/**
 * Shared parsing for the system-status (outage) banner.
 *
 * The banner is driven entirely by a PostHog feature flag + JSON payload so it
 * can be toggled — and its copy rewritten — with no deploy. That matters because
 * every backend service runs in Fly's `sjc` region (including the Prometheus /
 * Tempo monitoring stack), so during a regional outage the only control planes
 * still standing are Vercel and PostHog. The flag lives in PostHog for exactly
 * that reason: never move this switch behind our own API.
 *
 * Payload shape (all fields optional — an empty payload still yields a banner):
 *   { "message": "...", "severity": "info" | "warning" | "critical", "learnMoreUrl": "https://..." }
 */

export const SYSTEM_STATUS_FLAG = "system-status-banner";

export type SystemStatusSeverity = "info" | "warning" | "critical";

export interface SystemStatus {
  message: string;
  severity: SystemStatusSeverity;
  learnMoreUrl?: string;
}

const SEVERITIES: readonly string[] = ["info", "warning", "critical"];

/**
 * Shown when the flag is on but the payload is missing or unusable. Flipping the
 * switch during an incident must always surface *something* — an operator under
 * pressure should not have to author valid JSON to get a message on screen.
 */
export const DEFAULT_STATUS_MESSAGE =
  "We're having trouble reaching one of our providers, so some rates and bookings may not load right now. We're on it.";

const DEFAULT_SEVERITY: SystemStatusSeverity = "warning";

/**
 * Characters the URL parser treats as insignificant but a naive prefix check does
 * not, so allowing them through would let a payload smuggle past the checks in
 * `parseLearnMoreUrl`:
 *
 * - `\` is equivalent to `/` for special schemes, so `/\evil.com` starts with a
 *   single `/` yet resolves cross-origin — an open redirect.
 * - Tabs, newlines and other C0 controls are *stripped* during parsing, so
 *   `java\tscript:alert(1)` and `/\tevil` are not what they look like.
 *
 * A `learnMoreUrl` is a plain link; none of these belong in one. Reject outright
 * rather than normalise — this stays a pure function with no dependency on a DOM
 * origin, so it remains testable and behaves identically on the server.
 */
function hasUnsafeUrlChars(url: string): boolean {
  if (url.includes("\\")) return true;
  for (const char of url) {
    const code = char.codePointAt(0) ?? 0;
    // C0 controls + space (already trimmed at the edges, so this catches
    // *interior* whitespace) and DEL.
    if (code <= 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * `learnMoreUrl` arrives from remote config, so it is untrusted input on its way
 * into an `href`. Allow only absolute http(s) URLs and same-origin paths —
 * anything else (notably `javascript:` and `data:`) is dropped, not rendered.
 */
function parseLearnMoreUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const url = value.trim();
  if (!url) return undefined;
  if (hasUnsafeUrlChars(url)) return undefined;
  if (url.startsWith("/") && !url.startsWith("//")) return url;
  return /^https?:\/\//i.test(url) ? url : undefined;
}

/**
 * Resolve the banner from a flag value + payload.
 *
 * Returns `null` whenever the banner must not render — including while the flag
 * is still `undefined`. PostHog is initialised without `bootstrap`, so flags are
 * `undefined` on first paint; treating that as "off" is what keeps the banner
 * from flashing on every cold load.
 */
export function parseSystemStatus(
  enabled: boolean | undefined,
  payload: unknown
): SystemStatus | null {
  if (enabled !== true) return null;

  const raw =
    typeof payload === "object" && payload !== null && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)
      : {};

  const message =
    typeof raw.message === "string" && raw.message.trim()
      ? raw.message.trim()
      : DEFAULT_STATUS_MESSAGE;

  const severity =
    typeof raw.severity === "string" && SEVERITIES.includes(raw.severity)
      ? (raw.severity as SystemStatusSeverity)
      : DEFAULT_SEVERITY;

  const learnMoreUrl = parseLearnMoreUrl(raw.learnMoreUrl);

  return learnMoreUrl
    ? { message, severity, learnMoreUrl }
    : { message, severity };
}
